import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import { FormTypesData } from '../../domains/common.domain';
import { LocalStorageService } from '../../services/local-storage.service';
import { CommunicationService } from '../../services/communication.service';
import { ComponentFormData } from '../jurisdiction.domain';
import {
  BaseDataObject,
  FacilityDataObject,
  FiringSystemDataObject,
  FurnaceDataObject,
  GasDeviceData,
  GasDeviceObject,
  GetInstallationApplicationObject,
  HeatingDeviceObject,
  HeatingFacilityData,
  InstallationApplicationData,
  MeterDataObject,
  OldMeterData,
  WaterDeviceData,
  WaterDeviceObject,
} from './facilities-form.domain';
import { CommonService } from '../../services/common.service';
import { EnvironmentService } from '../../services/environment.service';

@Injectable({
  providedIn: 'root',
})
export class FacilitiesFormService {
  private installationApplicationObject: InstallationApplicationData;
  private gasBaseData: BaseDataObject[];
  private gasFacilityData: FacilityDataObject;
  private gasMeterData: MeterDataObject[];
  private heatingBaseData: BaseDataObject[];
  private heatingFacilityData: FacilityDataObject;
  private heatingMeterData: MeterDataObject[];
  private powerBaseData: BaseDataObject[];
  private powerFacilityData: FacilityDataObject;
  private gasFiringData: FiringSystemDataObject;
  private powerMeterData: MeterDataObject[];
  private gasFurnaceData: FurnaceDataObject[];
  private waterBaseData: BaseDataObject[];
  private waterFacilityData: FacilityDataObject;
  private waterMeterData: MeterDataObject[];
  private powerOldMeterData: OldMeterData;
  private gasOldMeterData: OldMeterData;
  private waterOldMeterData: OldMeterData;
  private heatingOldMeterData: OldMeterData;
  private showSkMeterMountHeat: boolean;
  private chimneyChoiceData: {
    sk_chim_sweep_confirm: string;
  };
  private currentFlowType: string;

  private triggerComponentFormUpdate$: Subject<ComponentFormData>;
  private triggerSpecialApplianceUpdate$: Subject<ComponentFormData>;
  private triggerBaseDataUpdate$: Subject<ComponentFormData>;
  private triggerGasDeviceUpdate$: Subject<ComponentFormData>;
  private triggerActionOnFormSubmit$: Subject<boolean>;
  private triggerWaterDeviceUpdate$: Subject<ComponentFormData>;
  private triggerDistrictHeatDeviceUpdate$: Subject<ComponentFormData>;

  constructor(
    private commonService: CommonService,
    private communicationService: CommunicationService,
    private environmentService: EnvironmentService,
    private localStorageService: LocalStorageService
  ) {
    this.heatingBaseData = [];
    this.waterBaseData = [];
    this.gasBaseData = [];
    this.powerBaseData = [];
    this.heatingFacilityData = null;
    this.waterFacilityData = null;
    this.gasFacilityData = null;
    this.powerFacilityData = null;
    this.gasFiringData = null;
    this.heatingMeterData = [];
    this.waterMeterData = [];
    this.gasMeterData = [];
    this.powerMeterData = [];
    this.powerOldMeterData = null;
    this.gasOldMeterData = null;
    this.waterOldMeterData = null;
    this.heatingOldMeterData = null;
    this.showSkMeterMountHeat = this.environmentService.showSkMeterMountHeat;
    this.currentFlowType = this.localStorageService.getItem('installation_flow');
    this.initializeFormSubscriptions();
  }

  initializeFormSubscriptions(): void {
    this.triggerComponentFormUpdate$ = new Subject<ComponentFormData>();
    this.triggerSpecialApplianceUpdate$ = new Subject<ComponentFormData>();
    this.triggerBaseDataUpdate$ = new Subject<ComponentFormData>();
    this.triggerGasDeviceUpdate$ = new Subject<ComponentFormData>();
    this.triggerActionOnFormSubmit$ = new Subject<boolean>();
    this.triggerWaterDeviceUpdate$ = new Subject<ComponentFormData>();
    this.triggerDistrictHeatDeviceUpdate$ = new Subject<ComponentFormData>();
  }

  get registerForComponentFormUpdate(): Subject<ComponentFormData> {
    return this.triggerComponentFormUpdate$;
  }

  get registerForSpecialApplianceUpdate(): Subject<ComponentFormData> {
    return this.triggerSpecialApplianceUpdate$;
  }

  get registerForBaseDataUpdate(): Subject<ComponentFormData> {
    return this.triggerBaseDataUpdate$;
  }

  get registerForGasDeviceUpdate(): Subject<ComponentFormData> {
    return this.triggerGasDeviceUpdate$;
  }

  get registerForActionOnFormSubmit(): Subject<boolean> {
    return this.triggerActionOnFormSubmit$;
  }

  get registerForWaterDeviceUpdate(): Subject<ComponentFormData> {
    return this.triggerWaterDeviceUpdate$;
  }

  get registerForDistrictHeatDeviceUpdate(): Subject<ComponentFormData> {
    return this.triggerDistrictHeatDeviceUpdate$;
  }

  set installationApplicationData(data: InstallationApplicationData) {
    this.installationApplicationObject = data;
  }

  manageComponentFormUpdate(data: ComponentFormData): void {
    this.triggerComponentFormUpdate$.next(data);
  }

  manageGasDeviceFormData(data: ComponentFormData): void {
    this.triggerGasDeviceUpdate$.next(data);
  }

  manageSpecialApplianceFormData(data: ComponentFormData): void {
    this.triggerSpecialApplianceUpdate$.next(data);
  }

  manageWaterDeviceFormData(data: ComponentFormData): void {
    this.triggerWaterDeviceUpdate$.next(data);
  }

  manageDistrictHeatDeviceFormData(data: ComponentFormData): void {
    this.triggerDistrictHeatDeviceUpdate$.next(data);
  }

  manageBaseDataFormData(data: ComponentFormData): void {
    this.triggerBaseDataUpdate$.next(data);
  }

  actionOnFormSubmit(): void {
    this.triggerActionOnFormSubmit$.next(true);
  }

  getFormData(formType: string): any {
    return {
      baseData: this[`${formType}BaseData`],
      chimneySweepData: this.chimneyChoiceData,
      meterData: this[`${formType}MeterData`],
      newFacility: this[`${formType}FacilityData`],
      oldMeterData: this[`${formType}OldMeterData`],
      firingData: this[`${formType}FiringData`],
      furnaceData: this[`${formType}FurnaceData`],
    };
  }

  getInstallationApplication(): Observable<GetInstallationApplicationObject> {
    const applicationId = this.localStorageService.getItem('application_id');

    return this.communicationService.getData(`installationapplication?application_id=${applicationId}`);
  }

  parseApplicationDataIntoForm(selectedFacilityTypes: FormTypesData[]): void {
    selectedFacilityTypes.forEach(type => {
      this.parseBaseData(type);
      this.parseFacilityData(type);
      if (this.environmentService.showNewMeterSection) {
        this.parseMeterData(type);
      }
      this.parseOldMeterData(type);
      if (this.environmentService.showTechnicalDataFeverPlantSection) {
        this.parseFiringSystemData(type);
        this.parseFurnaceData(type);
      }
    });
  }

  parseBaseData(facilityType: FormTypesData): void {
    switch (facilityType.index) {
      case 0:
        this.preparePowerBaseData();
        break;
      case 1:
        this.prepareGasBaseData();
        this.chimneyChoiceData = {
          sk_chim_sweep_confirm: this.installationApplicationObject?.gasinstallation?.sk_chim_sweep_confirm || '',
        };
        break;
      case 2:
        this.prepareWaterBaseData();
        break;
      case 3:
        this.prepareHeatingBaseData();
        break;
    }
  }

  preparePowerBaseData(): void {
    this.powerBaseData = [];
    const facilityData =
      (this.installationApplicationObject.powerinstallation && this.installationApplicationObject.powerinstallation.facility) || [];
    facilityData.forEach(facilityItem => {
      const specialConsumerObject = [];

      facilityItem.specialconsumer.forEach(specialConsumerDevice => {
        specialConsumerObject.push({
          deviceId: specialConsumerDevice.specialconsumer_id,
          facilityId: specialConsumerDevice.facility_id,
          specialAppliance: specialConsumerDevice.sk_special_consumer_type,
          typeName: specialConsumerDevice.type_name,
          manufacturer: specialConsumerDevice.manufacturer,
          amount: specialConsumerDevice.consumer_count,
          power: specialConsumerDevice.power,
          consumptionDeviceCheckbox: specialConsumerDevice.charger_controllabledevice === '1',
          publicDeviceCheckbox: specialConsumerDevice.charger_publicdevice === '1',
          blockIntervals: specialConsumerDevice.sk_swkn_block_intervals,
        });
      });

      this.powerBaseData.push({
        facilityId: facilityItem.facility_id,
        installationId: facilityItem.installation_id,
        facilityType: facilityItem.sk_power_facility_type,
        noOfConnections: facilityItem.transaction_no,
        sector: facilityItem.commercial_sector,
        registration: facilityItem.registration_no,
        internalConsumptionCheckbox: facilityItem.generation_withinternalconsumption === '1',
        storageSystemCheckbox: facilityItem.generation_storagesystem === '1',
        placeOfMeter: facilityItem.sk_device_location,
        specialAppliance: specialConsumerObject,
        otherLocation: facilityItem.other_location_details,
        otherType: facilityItem.other_type_details,
        simultaneouslyRequiredTotalPowerAfterInstallationChange: facilityItem.simultaneously_required_total_power_after_installation_change,
      });
    });
  }

  prepareGasBaseData(): void {
    const facilityData =
      (this.installationApplicationObject.gasinstallation && this.installationApplicationObject.gasinstallation.facility) || [];

    this.gasBaseData = facilityData.map(facilityItem => {
      const deviceObject: GasDeviceObject[] = facilityItem.device.map((item: GasDeviceData) => {
        let device = {
          deviceId: item.device_id,
          facilityId: item.facility_id,
          ceNumber: item.ce_number,
          deviceName: item.name,
          power: item.power,
          deviceRemovedCheckbox: item.device_removed === '1',
          selectedGasCategory: item.sk_selected_gas_category,
          applianceType: item.gas_device_type,
          manufacturer: item.sk_device_manufacturer,
          otherManufacturer: item.device_manufacturer_other,
          type: item.type,
          manufactureYear: item.construction_year,
          applianceCategory: item.sk_device_category,
          requiredFlowPressure: item.sk_required_flow_pressure,
          otherFlowPressure: item.other_pressure,
          gasUsageFor: item.sk_gas_usage_for,
        };

        return {
          ...device,
          customFields: { ...device },
        };
      });

      return {
        facilityId: facilityItem.facility_id,
        installationId: facilityItem.installation_id,
        facilityType: facilityItem.sk_gas_facility_type,
        oldPowerGridConnection: facilityItem.total_power_required_old,
        newPowerGridConnection: facilityItem.total_power_required_new,
        noOfConnections: facilityItem.transaction_no,
        placeOfMeter: facilityItem.sk_device_location,
        sector: facilityItem.commercial_sector,
        gasDevice: deviceObject,
        otherLocation: facilityItem.other_location_details,
        totalHeatOutput: facilityItem.total_power,
        isPowerConnectionForRemoteMeterReadingAvailable: facilityItem.power_supply_available,
      };
    });
  }

  prepareWaterBaseData(): void {
    const facilityData =
      (this.installationApplicationObject.waterinstallation && this.installationApplicationObject.waterinstallation.facility) || [];

    this.waterBaseData = facilityData.map(facilityItem => {
      const deviceObject: WaterDeviceObject[] = facilityItem.tappingpoint.map((item: WaterDeviceData) => {
        let device = {
          deviceId: item.tappingpoint_id,
          facilityId: item.facility_id,
          specialApplianceDropDown: item.sk_tappingpoint_type,
          amount: item.consumer_count,
          vrCold: item.vrcold,
          vrHot: item.vrhot,
          swknManufacturer: item.swkn_manufacturer,
          swknDWGVCertifiedCheckbox: item.swkn_dwgv_certified === '1',
          swknType: item.swkn_type,
          swknFlowRate: item.swkn_flow_rate,
          swknDeliveryPressure: item.swkn_delivery_pressure,
          swknReqExtinguishingVolume: item.swkn_req_extinguishing_volume,
          swknWaterFacilitySystemType: item.sk_swkn_water_facility_system_type,
        };

        return {
          ...device,
          customFields: { ...device },
        };
      });

      return {
        facilityId: facilityItem.facility_id,
        installationId: facilityItem.installation_id,
        facilityType: facilityItem.sk_water_facility_type,
        noOfConnections: facilityItem.transaction_no,
        placeOfMeter: facilityItem.sk_device_location,
        sector: facilityItem.commercial_sector,
        sumFlowRate: facilityItem.sumflowrate,
        peakFlowRate: facilityItem.peakflowrate,
        waterDevice: deviceObject,
        otherLocation: facilityItem.other_location_details,
        measuringPointCheckbox: facilityItem.sensitive_measuring_point === '1',
      };
    });
  }

  prepareHeatingBaseData(): void {
    this.heatingBaseData = [];
    const facilityData =
      (this.installationApplicationObject.districtheatinstallation &&
        this.installationApplicationObject.districtheatinstallation.facility) ||
      [];

    this.heatingBaseData = facilityData.map(facilityItem => ({
      facilityId: facilityItem.facility_id,
      installationId: facilityItem.installation_id,
      facilityType: facilityItem.sk_districtheat_facility_type,
      noOfConnections: facilityItem.transaction_no,
      placeOfMeter: facilityItem.sk_districtheat_device_location,
      sector: facilityItem.commercial_sector,
      heatingDevice: this.mapHeatingDevice(facilityItem),
      otherLocation: facilityItem.other_location_details,
    }));
  }

  private mapHeatingDevice(facilityItem: HeatingFacilityData): HeatingDeviceObject {
    const device = {
      dhType: facilityItem.sk_districtheat_distribution_type,
      dhTypeHeating: facilityItem.sk_districtheat_heating_type,
      heatingLoad: facilityItem.heating_load.replace('.', ','),
      installHeatPower: facilityItem.heating_power.replace('.', ','),
      floorHeating: facilityItem.floor_heating_power,
      dhw: facilityItem.drinking_water === '1',
      designTempTv: facilityItem.design_temperature_tv.replace('.', ','),
      dhwType: facilityItem.drinking_water_brand,
      designTempFloorHeatingTv: facilityItem.floor_design_temperature_tv,
      dhwvolume: facilityItem.drinking_water_volumen,
      designTempTr: facilityItem.design_temperature_tr.replace('.', ','),
      installDirections: facilityItem.swkn_districtheat_device_location_text,
      designTempFloorHeatingTr: facilityItem.floor_design_temperature_tr,
      thermoValveType: facilityItem.radiator_thermovalve_brand,
      flowRadiators: facilityItem.radiator_flowrate,
      deviation: facilityItem.radiator_deviation,
      flowFloorHeating: facilityItem.floor_fowrate,
    };

    return {
      ...device,
      customFields: { ...device },
    };
  }

  parseFiringSystemData(facilityType: FormTypesData): void {
    switch (facilityType.index) {
      case 1:
        const gasInstallation = this.installationApplicationObject?.gasinstallation;
        const exhaustRowKeys = [
          { key: 'innerShell', value: 'innershell_' },
          { key: 'insulatingMaterial', value: 'insualtion_' },
          { key: 'ringGap', value: 'ringgap_' },
          { key: 'shaft', value: 'outershell_' },
        ];
        const exhaustColKeys = [
          { key: 'buildingMaterial', value: 'bulding_material' },
          { key: 'thicknessInCm', value: 'thickness' },
          { key: 'producerType', value: 'manufacturer' },
          { key: 'proofOfUsability', value: 'proof_of_usability' },
        ];
        const rowKeys = {};
        for (const [rowKey, rowObj] of Object.entries(exhaustRowKeys)) {
          const columnKeys = {};
          for (const [colKey, colObj] of Object.entries(exhaustColKeys)) {
            const keyData = rowObj.value + colObj.value || '';
            const keyDataVal = keyData === 'outershell_thickness' ? 'sk_' + keyData : keyData;
            columnKeys[colObj.key] = gasInstallation?.fireingsystem && gasInstallation?.fireingsystem[keyDataVal];
          }
          rowKeys[rowObj.key] = columnKeys;
        }
        this.gasFiringData = {
          systemId: gasInstallation?.fireingsystem?.system_id || '',
          constructionChimneySweep: gasInstallation?.fireingsystem?.sk_construction_spec || '',
          chimneyCheckbox: gasInstallation?.fireingsystem?.chimney === '1',
          singleOccupancyCheckbox: gasInstallation?.fireingsystem?.single_occupancy === '1' || false,
          vacuumOperationCheckbox: gasInstallation?.fireingsystem?.vacuum_operation === '1' || false,
          moistureSensitiveCheckbox: gasInstallation?.fireingsystem?.moisture_sensitive === '1' || false,
          notMoistureSensitiveCheckbox: gasInstallation?.fireingsystem?.moisture_insensitive === '1' || false,
          emissionGasLineCheckbox: gasInstallation?.fireingsystem?.emission_gas_line === '1' || false,
          overPressureOperationCheckbox: gasInstallation?.fireingsystem?.overpressure_operation === '1' || false,
          multipleOccupancyCheckbox: gasInstallation?.fireingsystem?.multiple_occupancy === '1' || false,
          airEmissionSystemCheckbox: gasInstallation?.fireingsystem?.air_emission_system === '1' || false,
          assemblyLineCheckbox: gasInstallation?.fireingsystem?.assembly_line === '1' || false,
          systemUsabilityCheckbox: gasInstallation?.fireingsystem?.proof_of_usability_type === '1' || false,
          systemUsabilityTextValue: gasInstallation?.fireingsystem?.proof_of_usability_comment || '',
          effectiveHeight: gasInstallation?.fireingsystem?.effective_height?.replace('.', ',') || '',
          chimney: gasInstallation?.fireingsystem?.pipe_width || '',
          fireplace: gasInstallation?.fireingsystem?.sk_pipe_with_unit || '',
          dimension: gasInstallation?.fireingsystem?.sk_assessment_base || '',
          din: gasInstallation?.fireingsystem?.assessment_base_din_comment || '',
          withBlowerCheckbox: gasInstallation?.fireingsystem?.combustion_with_ventilation === '1' || false,
          withoutBlowerCheckbox: gasInstallation?.fireingsystem?.combustion_without_ventilation === '1' || false,
          withFlowProtectionCheckbox: gasInstallation?.fireingsystem?.combustion_with_flowprotection === '1' || false,
          withoutFlowProtectionCheckbox: gasInstallation?.fireingsystem?.combustion_without_flowprotection === '1' || false,
          airSupplyInstallationRoomCheckbox: gasInstallation?.fireingsystem?.combustion_air_supply_room === '1' || false,
          airSupplyOpenAirCheckbox: gasInstallation?.fireingsystem?.combustion_air_supply_open === '1' || false,
          emissionGasCheckbox: gasInstallation?.fireingsystem?.combustion_exhaust_combair === '1' || false,
          roomVentilation: gasInstallation?.fireingsystem?.sk_combustion_boiler_ventilation_type || '',
          roomVentilationValueincm: gasInstallation?.fireingsystem?.combustion_boiler_ventilation || '',
          approvalClient: gasInstallation?.fireingsystem?.written_approval_client === '1' || false,
          approvalPlanner: gasInstallation?.fireingsystem?.written_approval_planner === '1' || false,
          meterInstallation: gasInstallation?.fireingsystem?.has_gasmeter_installation,
          ...rowKeys,
        };
        break;
    }
  }

  parseFacilityData(facilityType: FormTypesData): void {
    switch (facilityType.index) {
      case 0:
        this.powerFacilityData = {
          installationId:
            this.installationApplicationObject.powerinstallation && this.installationApplicationObject.powerinstallation.installation_id,
          measuringPointOperator:
            this.installationApplicationObject.powerinstallation &&
            this.installationApplicationObject.powerinstallation.sk_measuring_point_operator,
          company:
            this.installationApplicationObject.powerinstallation &&
            this.installationApplicationObject.powerinstallation.measuring_point_company,
        };
        break;
      case 1:
        this.gasFacilityData = {
          installationId:
            this.installationApplicationObject.gasinstallation && this.installationApplicationObject.gasinstallation.installation_id,
          measuringPointOperator:
            this.installationApplicationObject.gasinstallation &&
            this.installationApplicationObject.gasinstallation.sk_measuring_point_operator,
          company:
            this.installationApplicationObject.gasinstallation &&
            this.installationApplicationObject.gasinstallation.measuring_point_company,
        };
        break;
      case 2:
        this.waterFacilityData = {
          installationId:
            this.installationApplicationObject.waterinstallation && this.installationApplicationObject.waterinstallation.installation_id,
          measuringPointOperator:
            this.installationApplicationObject.waterinstallation &&
            this.installationApplicationObject.waterinstallation.sk_measuring_point_operator,
          company:
            this.installationApplicationObject.waterinstallation &&
            this.installationApplicationObject.waterinstallation.measuring_point_company,
        };
        break;
      case 3:
        this.heatingFacilityData = {
          installationId:
            this.installationApplicationObject.districtheatinstallation &&
            this.installationApplicationObject.districtheatinstallation.installation_id,
          measuringPointOperator:
            this.installationApplicationObject.districtheatinstallation &&
            this.installationApplicationObject.districtheatinstallation.sk_measuring_point_operator,
          company:
            this.installationApplicationObject.districtheatinstallation &&
            this.installationApplicationObject.districtheatinstallation.measuring_point_company,
        };
        break;
    }
  }

  parseFurnaceData(facilityType: FormTypesData): void {
    let furnaceData: any[];
    switch (facilityType.index) {
      case 1:
        this.gasFurnaceData = [];
        furnaceData =
          (this.installationApplicationObject?.gasinstallation &&
            this.installationApplicationObject?.gasinstallation?.fireingsystem?.furnance) ||
          [];

        furnaceData.forEach(furnaceItem => {
          this.gasFurnaceData.push({
            furnaceId: furnaceItem.furnance_id,
            systemId: furnaceItem.system_id,
            temp: furnaceItem.emission_temp,
            proofOfUsability: furnaceItem.proof_of_usability,
            heat: furnaceItem.rated_heat1,
            setHeat: furnaceItem.rated_heat2,
            volume: furnaceItem.room_volume,
            otherFuelType: furnaceItem.combustion_other_fuel,
            fuel: furnaceItem.sk_fuel_type,
            producerType: furnaceItem.manufacturer,
            furnace: furnaceItem.type,
          });
        });
        break;
    }
  }

  parseMeterData(facilityType: FormTypesData): void {
    let meterData: any[];

    switch (facilityType.index) {
      case 0:
        this.powerMeterData = [];
        meterData =
          (this.installationApplicationObject.powerinstallation && this.installationApplicationObject.powerinstallation.meter) || [];
        meterData.forEach(meterItem => {
          this.powerMeterData.push({
            meterId: meterItem.meter_id,
            installationId: meterItem.installation_id,
            meterType: meterItem.sk_power_meter_type,
            amount: meterItem.meter_count,
            swknFloor: meterItem.swkn_floor_or_appnr,
            oldMeterNumber: meterItem.old_meter_number,
            selectMount: meterItem.sk_meter_mount,
            selectComm: meterItem.sk_communication,
            transformerFactor: meterItem.transducer_converterfactor,
            providedByCustomerCheckbox: meterItem.transducer_customerprovided === '1',
            customerName: meterItem.transducer_customername,
            providedByMsbCheckbox: meterItem.transducer_msbprovided === '1',
            msbName: meterItem.transducer_msbname,
            modernMeterSubtype: meterItem.sk_modern_meter_subtype,
            annualConsumption: meterItem.sk_estimated_annual_consumption,
            controlUnitAvailableCheckbox: meterItem.swkn_ctrl_unit_available === '1',
            transducerSize: meterItem.sk_transducer_size_a,
            rlmMeasurement: meterItem.rlm_measurement,
          });
        });
        break;
      case 1:
        this.gasMeterData = [];
        meterData = (this.installationApplicationObject.gasinstallation && this.installationApplicationObject.gasinstallation.meter) || [];

        meterData.forEach(meterItem => {
          this.gasMeterData.push({
            meterId: meterItem.meter_id,
            installationId: meterItem.installation_id,
            meterType: meterItem.sk_gas_meter_type,
            selectMount: meterItem.sk_meter_mount,
            selectComm: meterItem.sk_communication,
            amount: meterItem.meter_count,
            annualConsumptionForecast: meterItem.consumption_forecast,
            counterSize: meterItem.sk_gas_meter_size,
            remoteReadoutCheckbox: meterItem.remote_readout === '1',
            location: meterItem.position,
          });
        });
        break;
      case 2:
        this.waterMeterData = [];
        meterData =
          (this.installationApplicationObject.waterinstallation && this.installationApplicationObject.waterinstallation.meter) || [];

        meterData.forEach(meterItem => {
          this.waterMeterData.push({
            meterId: meterItem.meter_id,
            installationId: meterItem.installation_id,
            meterType: meterItem.sk_water_meter_type,
            amount: meterItem.meter_count,
            selectMount: meterItem.sk_meter_mount,
            selectComm: meterItem.sk_communication,
            annualConsumptionForecast: meterItem.consumption_forecast,
            counterSize: meterItem.sk_water_meter_size,
            otherType: meterItem.other_size_details,
            alignment: meterItem.sk_mounting_alignment,
            meterCompliance: meterItem.sk_standard_compliance,
            location: meterItem.location,
            swknRemoteReadoutCheckbox: meterItem.swkn_remote_readout === '1',
          });
        });
        break;
      case 3:
        this.heatingMeterData = [];
        meterData =
          (this.installationApplicationObject.districtheatinstallation &&
            this.installationApplicationObject.districtheatinstallation.meter) ||
          [];

        meterData.forEach(meterItem => {
          this.heatingMeterData.push({
            meterId: meterItem.meter_id,
            installationId: meterItem.installation_id,
            meterType: meterItem.sk_districtheat_meter_type,
            amount: meterItem.meter_count,
            selectMount: meterItem.sk_meter_mount,
            selectComm: meterItem.sk_communication,
            annualConsumptionForecast: meterItem.consumption_forecast,
            counterSize: meterItem.sk_heat_meter_size,
            requestedTotalPower: meterItem.requested_total_power,
          });
        });
        break;
    }
  }

  parseOldMeterData(facilityType: FormTypesData): void {
    let oldMeter: any;
    switch (facilityType.index) {
      case 0:
        oldMeter = this.installationApplicationObject.powerinstallation;
        this.powerOldMeterData = {
          replaced_meter_image_id: oldMeter.replaced_meter_image_id,
          replaced_meter_no: oldMeter.replaced_meter_no,
          replaced_meter_reading: oldMeter.replaced_meter_reading,
        };
        break;
      case 1:
        oldMeter = this.installationApplicationObject.gasinstallation;
        this.gasOldMeterData = {
          replaced_meter_image_id: oldMeter.replaced_meter_image_id,
          replaced_meter_no: oldMeter.replaced_meter_no,
          replaced_meter_reading: oldMeter.replaced_meter_reading,
        };
        break;
      case 2:
        oldMeter = this.installationApplicationObject.waterinstallation;
        this.waterOldMeterData = {
          replaced_meter_image_id: oldMeter.replaced_meter_image_id,
          replaced_meter_no: oldMeter.replaced_meter_no,
          replaced_meter_reading: oldMeter.replaced_meter_reading,
        };
        break;
      case 3:
        oldMeter = this.installationApplicationObject.districtheatinstallation;
        this.heatingOldMeterData = {
          replaced_meter_image_id: oldMeter.replaced_meter_image_id,
          replaced_meter_no: oldMeter.replaced_meter_no,
          replaced_meter_reading: oldMeter.replaced_meter_reading,
        };
        break;
    }
  }

  saveData(urlSegment: string, requestData: any): Observable<any> {
    return this.communicationService.postData(urlSegment, requestData);
  }

  updateData(urlSegment: string, requestData: any): Observable<any> {
    return this.communicationService.updateData(urlSegment, requestData);
  }
}
